<script setup>
import { ref, reactive, computed, onMounted, watch, onActivated } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import { useBase, dataHora } from './base';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

import CalibracaoAPI from 'dashboard/api/fandata/calibracao';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import PlaybookEntriesAPI from 'dashboard/api/fandata/playbookEntries';
import SandboxAPI from 'dashboard/api/fandata/sandbox';

const { t } = useI18n();
const { irPara } = useBase();
const route = useRoute();
const router = useRouter();
const store = useStore();

// R6.4 — o playbook também entra na versão: com ?versao=rascunho a lista abaixo é a do
// sandbox, e o que o Bira usa agora fica intocado até alguém publicar.
const versaoPlaybook = computed(() =>
  route.query.versao === 'rascunho' ? 'rascunho' : null
);
const temRascunho = ref(false);

const irParaPlaybook = destino =>
  router.replace({
    query: destino === 'rascunho' ? { versao: 'rascunho' } : {},
  });

const teams = useMapGetter('teams/getTeams');

const AUDIENCES = ['todos', 'socio', 'nao_socio'];

const isFetching = ref(true);

// Roteamento automatico por assunto (Cenario A).
const roteamento = reactive({ auto: false, map: {} });
const assuntos = ref([]);
const savingRoteamento = ref(false);

// R3.9 — alerta de fila (nao atribuidas aguardando humano). 0 = desligado.
const fila = reactive({ limite: 0 });
// T-159: a ordem da fila é uma regra do clube e mora junto do resto do comportamento da fila.
const ordenacao = reactive({ valor: 'last_activity_at_desc', opcoes: [] });
// T-165: para que serve cada canal. Receptivo é o padrão; só o clube sabe qual caixa existe para
// disparar.
const canais = reactive({ lista: [], uso: {}, usos: [] });
// T-127: quem recebe o relatório diário. Sem destinatário, nada é enviado.
const saude = reactive({ destinatarios: '', ativo: false });
const savingFila = ref(false);

// T-218 — gasto de IA do dia. Só leitura: quem manda no teto é o ambiente, não a tela.
const consumoIa = ref(null);

// O consumo por PERÍODO, com calendário. O cartão acima responde "como estamos hoje", que é a
// pergunta do teto; este responde "quanto custou na semana", que é outra.
//
// ⚠️ A JANELA É CURTA. O consumo vive em contador do Redis, que é onde precisa estar para somar
// de forma atômica com vários processos em paralelo; guardar para sempre exigiria tabela
// própria. A tela diz há quantos dias existe histórico, em vez de deixar a pessoa escolher uma
// data antiga e receber zero achando que não houve gasto.
const consumoPeriodo = reactive({
  dias: [],
  total_usd: 0,
  total_chamadas: 0,
  carregando: false,
});
const intervaloConsumo = ref([subDays(new Date(), 29), new Date()]);
const tipoIntervaloConsumo = ref(DATE_RANGE_TYPES.LAST_30_DAYS);

const carregarConsumo = async () => {
  consumoPeriodo.carregando = true;
  try {
    const { data } = await CalibracaoAPI.consumo({
      since: getUnixStartOfDay(intervaloConsumo.value[0]),
      until: getUnixEndOfDay(intervaloConsumo.value[1]),
    });
    consumoPeriodo.dias = data.dias || [];
    consumoPeriodo.total_usd = data.total_usd || 0;
    consumoPeriodo.total_chamadas = data.total_chamadas || 0;
  } catch (e) {
    consumoPeriodo.dias = [];
  } finally {
    consumoPeriodo.carregando = false;
  }
};

const aoTrocarPeriodoConsumo = valor => {
  const [inicio, fim, tipo] = valor;
  intervaloConsumo.value = [inicio, fim];
  tipoIntervaloConsumo.value = tipo || DATE_RANGE_TYPES.CUSTOM_RANGE;
  carregarConsumo();
};

// Os dias com gasto, do maior para o menor: é assim que se acha o dia fora da curva.
const diasComGasto = computed(() =>
  (consumoPeriodo.dias || [])
    .filter(d => d.gasto_usd > 0 || d.chamadas > 0)
    .slice()
    .sort((a, b) => b.gasto_usd - a.gasto_usd)
);

const dataCurta = iso => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

// T-211 — a pergunta do Bira sobre a própria precisão. Nasce desligada: fala com torcedor.
const avaliacaoBira = reactive({ ativa: false, salvando: false });

const gastoDoDia = computed(() => {
  if (!consumoIa.value) return '';
  const { gasto_usd: gasto, teto_usd: teto } = consumoIa.value;
  if (!teto) return `US$ ${gasto.toFixed(2)}`;
  return t('CAPITAO_SETTINGS.CONSUMO_IA.GASTO', {
    gasto: gasto.toFixed(2),
    teto: teto.toFixed(2),
  });
});

const corDoConsumo = computed(() => {
  const estado = consumoIa.value?.estado;
  if (estado === 'estourado') return 'text-n-ruby-11';
  if (estado === 'aviso') return 'text-n-amber-11';
  return 'text-n-slate-11';
});

const recadoDoConsumo = computed(() => {
  if (!consumoIa.value) return '';
  const base = 'CAPITAO_SETTINGS.CONSUMO_IA';
  if (!consumoIa.value.ligada) return t(`${base}.DESLIGADA`);
  if (!consumoIa.value.teto_usd) return t(`${base}.SEM_TETO`);
  if (consumoIa.value.estado === 'estourado') return t(`${base}.ESTOURADO`);
  if (consumoIa.value.estado === 'aviso') {
    return t(`${base}.AVISO`, {
      fatia: Math.round(consumoIa.value.fatia_do_aviso * 100),
    });
  }
  return t(`${base}.OK`);
});

// R8.4 — pesos da criticidade (3 mapas). Override por conta; vale para classificacoes NOVAS.
const pesos = reactive({ sentimento: {}, assunto: {}, prioridade: {} });
const pesosDefault = ref({});
const savingPesos = ref(false);
const SENTIMENTO_LABELS = {
  negativo: 'Negativo',
  neutro: 'Neutro',
  positivo: 'Positivo',
};
const PRIORIDADE_LABELS = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};
const assuntoTitulo = value =>
  (assuntos.value.find(a => a.value === value) || {}).title || value;

const entries = ref([]);

// Modal de criar/editar entrada
const showForm = ref(false);
const editingId = ref(null);
const savingForm = ref(false);
const form = reactive({
  title: '',
  category: '',
  content: '',
  audience: 'todos',
  enabled: true,
});

// Modal de exclusao
const showDelete = ref(false);
const selected = ref(null);
const deleting = ref(false);

const tableHeaders = computed(() => [
  t('CAPITAO_SETTINGS.PLAYBOOK.TABLE.TITLE'),
  t('CAPITAO_SETTINGS.PLAYBOOK.TABLE.CATEGORY'),
  t('CAPITAO_SETTINGS.PLAYBOOK.TABLE.AUDIENCE'),
  t('CAPITAO_SETTINGS.PLAYBOOK.TABLE.STATUS'),
  t('CAPITAO_SETTINGS.PLAYBOOK.TABLE.ACTIONS'),
]);

const audienceLabel = audience =>
  t(
    `CAPITAO_SETTINGS.PLAYBOOK.AUDIENCE.${(audience || 'todos').toUpperCase()}`
  );

const formTitle = computed(() =>
  editingId.value
    ? t('CAPITAO_SETTINGS.PLAYBOOK.FORM.EDIT_TITLE')
    : t('CAPITAO_SETTINGS.PLAYBOOK.FORM.NEW_TITLE')
);

const isFormValid = computed(() => form.title.trim() && form.content.trim());

// Esta tela é a porta do Bira, e porta tem que dizer o que está do outro lado. O que interessa
// aqui é a VOZ: qual versão dela está valendo e desde quando. O estado da base (quantas
// respostas, qual publicação) é assunto da Base de conhecimento, e repetir aqui só criaria dois
// lugares para a mesma informação divergirem.
const voz = reactive({ desde: null, rotulo: '', autor: '', versao_id: null });
const rascunhoDaVoz = ref(null);
const descartandoVoz = ref(false);

// Descartar o rascunho da voz. Fica aqui, na porta do Bira, pelo mesmo motivo que o da base fica
// na tela de versões da base: é onde se enxerga que existe um, e quem desiste desiste daqui.
const descartarVoz = async () => {
  descartandoVoz.value = false;
  try {
    await SandboxAPI.descartar('comportamento');
    useAlert(t('CAPITAO_SETTINGS.VOICE.DESCARTADO'));
    await loadAll();
  } catch (error) {
    useAlert(t('CAPITAO_SETTINGS.VOICE.ERRO_DESCARTAR'));
  }
};
const vozes = ref([]);

const loadAll = async () => {
  isFetching.value = true;
  try {
    const [settings, list, sandbox, comportamento] = await Promise.all([
      CalibracaoAPI.show(),
      PlaybookEntriesAPI.get(versaoPlaybook.value),
      SandboxAPI.show(),
      SandboxAPI.comportamento(),
    ]);
    // Dois rascunhos, dois sentidos: o playbook mora no rascunho DA BASE (é conteúdo) e o aviso
    // da seção de voz fala do rascunho DO COMPORTAMENTO. Misturar os dois faria a tela avisar
    // que a voz está em edição porque alguém mexeu numa resposta.
    temRascunho.value = !!sandbox.data.rascunho;
    rascunhoDaVoz.value = comportamento.data.atual?.rascunho || null;
    Object.assign(voz, comportamento.data.atual || {});
    vozes.value = (comportamento.data.historico || []).filter(
      entrada => !entrada.no_ar
    );
    // rascunho fechado em outra aba: volta para o playbook no ar em vez de errar a cada save
    if (versaoPlaybook.value && !temRascunho.value) irParaPlaybook('no_ar');
    roteamento.auto = !!settings.data.roteamento_auto;
    roteamento.map = { ...(settings.data.roteamento_map || {}) };
    assuntos.value = settings.data.assuntos || [];
    fila.limite = settings.data.alerta_fila_limite || 0;
    ordenacao.valor = settings.data.ordenacao_fila || 'last_activity_at_desc';
    ordenacao.opcoes = settings.data.ordenacao_opcoes || [];
    canais.lista = settings.data.canais || [];
    canais.usos = settings.data.canais_usos || [];
    canais.uso = { ...(settings.data.canais_uso || {}) };
    saude.destinatarios = (settings.data.saude_destinatarios || []).join(', ');
    saude.ativo = !!settings.data.saude_ativo;
    consumoIa.value = settings.data.consumo_ia || null;
    avaliacaoBira.ativa = !!settings.data.avaliacao_bira_ativa;
    const cp = settings.data.criticidade_pesos || {};
    pesos.sentimento = { ...(cp.sentimento || {}) };
    pesos.assunto = { ...(cp.assunto || {}) };
    pesos.prioridade = { ...(cp.prioridade || {}) };
    pesosDefault.value = settings.data.criticidade_pesos_default || {};
    entries.value = list.data || [];
  } catch (error) {
    useAlert(t('CAPITAO_SETTINGS.VOICE.ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const salvarAvaliacaoBira = async () => {
  avaliacaoBira.salvando = true;
  try {
    const { data } = await CalibracaoAPI.update({
      avaliacao_bira_ativa: avaliacaoBira.ativa,
    });
    avaliacaoBira.ativa = !!data.avaliacao_bira_ativa;
    useAlert(t('CAPITAO_SETTINGS.AVALIACAO_BIRA.SAVED'));
  } catch (error) {
    // Volta o interruptor: deixar ligado na tela e desligado no servidor é pior que o erro.
    avaliacaoBira.ativa = !avaliacaoBira.ativa;
    useAlert(t('CAPITAO_SETTINGS.AVALIACAO_BIRA.ERROR'));
  } finally {
    avaliacaoBira.salvando = false;
  }
};

const saveRoteamento = async () => {
  savingRoteamento.value = true;
  try {
    // Remove mapeamentos sem time (string vazia) antes de enviar.
    const map = Object.fromEntries(
      Object.entries(roteamento.map).filter(([, v]) => v)
    );
    const { data } = await CalibracaoAPI.update({
      roteamento_auto: roteamento.auto,
      roteamento_map: map,
    });
    roteamento.auto = !!data.roteamento_auto;
    roteamento.map = { ...(data.roteamento_map || {}) };
    useAlert(t('CAPITAO_SETTINGS.ROTEAMENTO.SAVED'));
  } catch (error) {
    useAlert(t('CAPITAO_SETTINGS.ROTEAMENTO.ERROR'));
  } finally {
    savingRoteamento.value = false;
  }
};

const savePesos = async () => {
  savingPesos.value = true;
  try {
    const payload = {
      sentimento: { ...pesos.sentimento },
      assunto: { ...pesos.assunto },
      prioridade: { ...pesos.prioridade },
    };
    const { data } = await CalibracaoAPI.update({ criticidade_pesos: payload });
    const cp = data.criticidade_pesos || {};
    pesos.sentimento = { ...(cp.sentimento || {}) };
    pesos.assunto = { ...(cp.assunto || {}) };
    pesos.prioridade = { ...(cp.prioridade || {}) };
    useAlert(t('CAPITAO_SETTINGS.PESOS.SAVED'));
  } catch (error) {
    useAlert(t('CAPITAO_SETTINGS.PESOS.ERROR'));
  } finally {
    savingPesos.value = false;
  }
};

const restaurarPesos = () => {
  const def = pesosDefault.value || {};
  pesos.sentimento = { ...(def.sentimento || {}) };
  pesos.assunto = { ...(def.assunto || {}) };
  pesos.prioridade = { ...(def.prioridade || {}) };
  savePesos();
};

const saveFila = async () => {
  savingFila.value = true;
  try {
    const { data } = await CalibracaoAPI.update({
      alerta_fila_limite: Number(fila.limite) || 0,
      ordenacao_fila: ordenacao.valor,
      canais_uso: canais.uso,
      saude_destinatarios: saude.destinatarios,
    });
    fila.limite = data.alerta_fila_limite || 0;
    ordenacao.valor = data.ordenacao_fila || ordenacao.valor;
    canais.uso = { ...(data.canais_uso || {}) };
    saude.destinatarios = (data.saude_destinatarios || []).join(', ');
    saude.ativo = !!data.saude_ativo;
    useAlert(t('CAPITAO_SETTINGS.FILA.SAVED'));
  } catch (error) {
    useAlert(t('CAPITAO_SETTINGS.FILA.ERROR'));
  } finally {
    savingFila.value = false;
  }
};

const irParaComportamento = () => irPara('capitao_base_comportamento');

const resetForm = () => {
  form.title = '';
  form.category = '';
  form.content = '';
  form.audience = 'todos';
  form.enabled = true;
  editingId.value = null;
};

const openNew = () => {
  resetForm();
  showForm.value = true;
};

const openEdit = entry => {
  editingId.value = entry.id;
  form.title = entry.title;
  form.category = entry.category || '';
  form.content = entry.content;
  form.audience = entry.audience || 'todos';
  form.enabled = entry.enabled;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  resetForm();
};

const saveEntry = async () => {
  if (!isFormValid.value) return;
  savingForm.value = true;
  const payload = {
    title: form.title.trim(),
    category: form.category.trim(),
    content: form.content.trim(),
    audience: form.audience,
    enabled: form.enabled,
  };
  try {
    if (editingId.value) {
      const { data } = await PlaybookEntriesAPI.update(
        editingId.value,
        payload,
        versaoPlaybook.value
      );
      entries.value = entries.value.map(e => (e.id === data.id ? data : e));
    } else {
      const { data } = await PlaybookEntriesAPI.create(
        payload,
        versaoPlaybook.value
      );
      entries.value = [data, ...entries.value];
    }
    useAlert(t('CAPITAO_SETTINGS.PLAYBOOK.FORM.SAVED'));
    closeForm();
  } catch (error) {
    useAlert(t('CAPITAO_SETTINGS.PLAYBOOK.FORM.ERROR'));
  } finally {
    savingForm.value = false;
  }
};

const openDelete = entry => {
  selected.value = entry;
  showDelete.value = true;
};

const closeDelete = () => {
  showDelete.value = false;
  selected.value = null;
};

const confirmDelete = async () => {
  if (!selected.value) return;
  deleting.value = true;
  try {
    await PlaybookEntriesAPI.delete(selected.value.id, versaoPlaybook.value);
    entries.value = entries.value.filter(e => e.id !== selected.value.id);
    useAlert(t('CAPITAO_SETTINGS.PLAYBOOK.DELETE.SUCCESS'));
    closeDelete();
  } catch (error) {
    useAlert(t('CAPITAO_SETTINGS.PLAYBOOK.DELETE.ERROR'));
  } finally {
    deleting.value = false;
  }
};

watch(versaoPlaybook, loadAll);

// Esta tela vive dentro do <keep-alive> das Configurações: voltar para ela não remonta o
// componente. Sem recarregar ao reativar, publicar e voltar mostraria a lista de antes.
let jaMontou = false;

onActivated(() => {
  if (!jaMontou) {
    jaMontou = true;
    return;
  }
  loadAll();
});

onMounted(() => {
  loadAll();
  carregarConsumo();
  store.dispatch('teams/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('CAPITAO_SETTINGS.HEADER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CAPITAO_SETTINGS.HEADER')"
        :description="$t('CAPITAO_SETTINGS.DESCRIPTION')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 mt-4">
        <!-- A voz do Bira, em versões.
             Abrir esta tela não despeja os textos: mostra QUAL voz está valendo e desde quando,
             e a lista das que já valeram. O texto de cada uma se lê clicando, com a anterior ao
             lado, que é o que responde "o que mudou". -->
        <section
          class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-wrap items-start gap-3">
            <div class="flex-1 min-w-[280px]">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ $t('CAPITAO_SETTINGS.VOICE.TITLE') }}
              </h3>
              <p v-if="voz.desde" class="mt-1 text-sm text-n-slate-11">
                {{
                  $t('CAPITAO_SETTINGS.VOICE.DESDE', {
                    quando: dataHora(voz.desde),
                    rotulo: voz.rotulo || '—',
                  })
                }}
                <template v-if="voz.autor">
                  · {{ $t('CAPITAO_SETTINGS.VOICE.POR', { autor: voz.autor }) }}
                </template>
              </p>
              <p v-else class="mt-1 text-sm text-n-slate-11">
                {{ $t('CAPITAO_SETTINGS.VOICE.NUNCA') }}
              </p>
            </div>
            <Button
              :label="$t('CAPITAO_SETTINGS.VOICE.VER_NO_AR')"
              variant="faded"
              color="slate"
              size="sm"
              @click="irPara('capitao_bira_voz', { id: 'atual' })"
            />
            <Button
              :label="$t('CAPITAO_SETTINGS.VOICE.IR')"
              variant="faded"
              color="amber"
              size="sm"
              @click="irParaComportamento"
            />
          </div>

          <!-- O rascunho em aberto, para poder ser retomado. Sem isto ele existia e não tinha
               onde ser clicado: quem saiu da tela de edição não achava o caminho de volta. -->
          <div
            v-if="rascunhoDaVoz"
            class="rounded-lg border border-solid border-n-amber-6 bg-n-amber-2 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2"
          >
            <div class="flex-1 min-w-[260px]">
              <div class="flex items-center gap-2.5 flex-wrap">
                <span
                  class="font-mono text-[10.5px] tracking-[.06em] px-1.5 py-0.5 rounded-sm bg-n-amber-3 text-n-amber-11"
                >
                  {{ $t('CAPITAO_SETTINGS.VOICE.ETIQUETA') }}
                </span>
                <span class="text-sm font-semibold text-n-slate-12">
                  {{
                    rascunhoDaVoz.rotulo ||
                    $t('CAPITAO_SETTINGS.VOICE.SEM_ROTULO')
                  }}
                </span>
              </div>
              <p class="mt-1 text-xs text-n-amber-11/70">
                <template v-if="rascunhoDaVoz.campos?.length">
                  {{
                    $t('CAPITAO_SETTINGS.VOICE.RASCUNHO_MUDOU', {
                      campos: rascunhoDaVoz.campos
                        .map(campo =>
                          $t(
                            `CAPITAO_SETTINGS.VOICE.${campo.toUpperCase()}_LABEL`
                          )
                        )
                        .join(', '),
                    })
                  }}
                </template>
                <template v-else>
                  {{ $t('CAPITAO_SETTINGS.VOICE.RASCUNHO_VAZIO') }}
                </template>
                · {{ $t('CAPITAO_SETTINGS.VOICE.RASCUNHO') }}
              </p>
            </div>
            <Button
              :label="$t('CAPITAO_SETTINGS.VOICE.CONTINUAR')"
              variant="faded"
              color="amber"
              size="sm"
              @click="irParaComportamento"
            />
            <Button
              v-if="rascunhoDaVoz.campos?.length"
              :label="$t('CAPITAO_SETTINGS.VOICE.VER_MUDANCAS')"
              variant="faded"
              color="amber"
              size="sm"
              @click="irPara('capitao_bira_voz_publicar')"
            />
            <Button
              :label="$t('CAPITAO_SETTINGS.VOICE.DESCARTAR')"
              variant="link"
              color="slate"
              size="sm"
              @click="descartandoVoz = true"
            />
          </div>

          <div
            v-if="descartandoVoz"
            class="rounded-lg border border-solid border-n-ruby-6 bg-n-ruby-3 px-4 py-3 flex flex-col gap-2"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('CAPITAO_SETTINGS.VOICE.DESCARTAR_TITULO') }}
            </p>
            <p class="text-[12.5px] text-n-slate-11">
              {{ $t('CAPITAO_SETTINGS.VOICE.DESCARTAR_CORPO') }}
            </p>
            <div class="flex flex-wrap gap-2 mt-1">
              <Button
                :label="$t('CAPITAO_SETTINGS.VOICE.DESCARTAR_CANCELAR')"
                variant="faded"
                color="slate"
                size="sm"
                @click="descartandoVoz = false"
              />
              <Button
                :label="$t('CAPITAO_SETTINGS.VOICE.DESCARTAR_CONFIRMAR')"
                color="ruby"
                size="sm"
                @click="descartarVoz"
              />
            </div>
          </div>

          <div class="flex flex-col">
            <span
              class="text-xs uppercase tracking-wide text-n-slate-10 font-medium mb-1"
            >
              {{ $t('CAPITAO_SETTINGS.VOICE.MUDANCAS') }}
            </span>
            <p v-if="!vozes.length" class="text-sm text-n-slate-11 py-2">
              {{ $t('CAPITAO_SETTINGS.VOICE.SEM_MUDANCAS') }}
            </p>
            <div
              v-if="vozes.length"
              class="grid grid-cols-[150px_minmax(0,1fr)_180px_160px] gap-3 px-2 pb-1 font-mono text-[10.5px] uppercase tracking-[.06em] text-n-slate-10"
            >
              <span>{{ $t('CAPITAO_SETTINGS.VOICE.COL_QUANDO') }}</span>
              <span>{{ $t('CAPITAO_SETTINGS.VOICE.COL_NOME') }}</span>
              <span>{{ $t('CAPITAO_SETTINGS.VOICE.COL_AUTOR') }}</span>
              <span>{{ $t('CAPITAO_SETTINGS.VOICE.COL_MUDOU') }}</span>
            </div>
            <button
              v-for="entrada in vozes"
              :key="entrada.versao_id"
              type="button"
              class="w-full text-left grid grid-cols-[150px_minmax(0,1fr)_180px_160px] gap-3 items-center px-2 py-2.5 rounded-lg border-b border-solid border-n-weak last:border-b-0 hover:bg-n-alpha-1"
              @click="irPara('capitao_bira_voz', { id: entrada.versao_id })"
            >
              <span class="text-sm text-n-slate-12 whitespace-nowrap">
                {{ dataHora(entrada.quando) }}
              </span>
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ entrada.rotulo }}
              </span>
              <span class="text-sm text-n-slate-11 truncate">
                {{ entrada.autor || '—' }}
              </span>
              <span class="text-[12.5px] text-n-amber-11 truncate">
                {{
                  entrada.campos
                    .map(campo =>
                      $t(`CAPITAO_SETTINGS.VOICE.${campo.toUpperCase()}_LABEL`)
                    )
                    .join(', ')
                }}
              </span>
            </button>
          </div>
        </section>

        <!-- Roteamento automático por assunto (Cenário A) -->
        <section
          class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex flex-col gap-1">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ $t('CAPITAO_SETTINGS.ROTEAMENTO.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('CAPITAO_SETTINGS.ROTEAMENTO.SUBTITLE') }}
              </p>
            </div>
            <label class="flex items-center gap-2 mt-1 flex-shrink-0">
              <input v-model="roteamento.auto" type="checkbox" />
              <span class="text-sm text-n-slate-12">
                {{ $t('CAPITAO_SETTINGS.ROTEAMENTO.ENABLED') }}
              </span>
            </label>
          </div>

          <div
            class="flex flex-col gap-2"
            :class="{ 'opacity-50': !roteamento.auto }"
          >
            <div
              v-for="a in assuntos"
              :key="a.value"
              class="flex items-center gap-3"
            >
              <span
                class="text-sm text-n-slate-12 capitalize w-32 flex-shrink-0"
              >
                {{ a.title }}
              </span>
              <select
                v-model="roteamento.map[a.value]"
                :disabled="!roteamento.auto"
                class="flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
              >
                <option value="">
                  {{ $t('CAPITAO_SETTINGS.ROTEAMENTO.NO_TEAM') }}
                </option>
                <option v-for="team in teams" :key="team.id" :value="team.id">
                  {{ team.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="flex justify-end">
            <Button
              :label="$t('CAPITAO_SETTINGS.ROTEAMENTO.SAVE')"
              size="sm"
              :is-loading="savingRoteamento"
              @click="saveRoteamento"
            />
          </div>
        </section>

        <!-- R3.9 — Alerta de fila (não atribuídas aguardando humano) -->
        <section
          class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-col gap-1">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('CAPITAO_SETTINGS.FILA.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ $t('CAPITAO_SETTINGS.FILA.SUBTITLE') }}
            </p>
          </div>
          <label class="flex flex-col gap-1 max-w-xs">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('CAPITAO_SETTINGS.FILA.LIMIT_LABEL') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('CAPITAO_SETTINGS.FILA.LIMIT_HINT') }}
            </span>
            <input
              v-model="fila.limite"
              type="number"
              min="0"
              class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
          </label>
          <label class="flex flex-col gap-1 max-w-xs">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('CAPITAO_SETTINGS.FILA.ORDEM_LABEL') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('CAPITAO_SETTINGS.FILA.ORDEM_HINT') }}
            </span>
            <select
              v-model="ordenacao.valor"
              class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
            >
              <option v-for="op in ordenacao.opcoes" :key="op" :value="op">
                {{ $t(`CAPITAO_SETTINGS.FILA.ORDEM_OPCOES.${op}`) }}
              </option>
            </select>
          </label>
          <div v-if="canais.lista.length" class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('CAPITAO_SETTINGS.FILA.CANAIS_LABEL') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('CAPITAO_SETTINGS.FILA.CANAIS_HINT') }}
            </span>
            <div
              v-for="canal in canais.lista"
              :key="canal.id"
              class="flex items-center justify-between gap-3 max-w-lg"
            >
              <span class="text-sm text-n-slate-12 truncate">
                {{ canal.nome }}
              </span>
              <select
                :value="canais.uso[String(canal.id)] || 'receptivo'"
                class="w-56 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
                @change="canais.uso[String(canal.id)] = $event.target.value"
              >
                <option v-for="uso in canais.usos" :key="uso" :value="uso">
                  {{ $t(`CAPITAO_SETTINGS.FILA.CANAIS_USOS.${uso}`) }}
                </option>
              </select>
            </div>
          </div>
          <label class="flex flex-col gap-1 max-w-lg">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('CAPITAO_SETTINGS.FILA.SAUDE_LABEL') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('CAPITAO_SETTINGS.FILA.SAUDE_HINT') }}
            </span>
            <input
              v-model="saude.destinatarios"
              type="text"
              placeholder="tamara@botafogo.com.br, caio@botafogo.com.br"
              class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
            />
            <span v-if="!saude.ativo" class="text-xs text-n-amber-11">
              {{ $t('CAPITAO_SETTINGS.FILA.SAUDE_DESLIGADO') }}
            </span>
          </label>
          <div class="flex justify-end">
            <Button
              :label="$t('CAPITAO_SETTINGS.FILA.SAVE')"
              size="sm"
              :is-loading="savingFila"
              @click="saveFila"
            />
          </div>
        </section>

        <!-- T-218 — Consumo de IA do dia.
             Fica ao lado do alerta de fila porque é o outro aviso da tela, e é só leitura: o
             teto e o preço são configuração de ambiente, não de conta. Mudar por aqui daria a
             quem administra o poder de desligar o freio de custo sem deixar rastro. -->
        <section
          v-if="consumoIa"
          class="flex flex-col gap-3 p-5 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div>
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('CAPITAO_SETTINGS.CONSUMO_IA.TITLE') }}
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ $t('CAPITAO_SETTINGS.CONSUMO_IA.SUBTITLE') }}
            </p>
          </div>
          <div class="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span
              class="text-2xl font-medium tabular-nums"
              :class="corDoConsumo"
            >
              {{ gastoDoDia }}
            </span>
            <span class="text-sm text-n-slate-11 tabular-nums">
              {{
                $t('CAPITAO_SETTINGS.CONSUMO_IA.CHAMADAS', {
                  n: consumoIa.chamadas,
                  teto: consumoIa.teto_de_chamadas,
                })
              }}
            </span>
          </div>
          <p class="text-sm" :class="corDoConsumo">{{ recadoDoConsumo }}</p>

          <!-- ⚠️ O valor em dólar é ESTIMATIVA, e a tela precisa dizer isso. O token é medido
               de verdade (vem da resposta da API); o dinheiro é conta nossa, com o preço do
               modelo configurado no ambiente. Sem este aviso, alguém leva o número para uma
               conversa de orçamento como se fosse fatura. -->
          <p class="pt-3 text-xs border-t text-n-slate-10 border-n-weak">
            {{ $t('CAPITAO_SETTINGS.CONSUMO_IA.ESTIMATIVA') }}
          </p>

          <!-- O consumo por período. O cartão acima responde "como estamos hoje", que é a
               pergunta do teto; este responde "quanto custou na semana". -->
          <div class="flex flex-col gap-3 pt-3 border-t border-n-weak">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <h4 class="text-sm font-medium text-n-slate-12">
                {{ $t('CAPITAO_SETTINGS.CONSUMO_IA.PERIODO_TITULO') }}
              </h4>
              <WootDatePicker
                v-model:date-range="intervaloConsumo"
                v-model:range-type="tipoIntervaloConsumo"
                @date-range-changed="aoTrocarPeriodoConsumo"
              />
            </div>

            <div
              v-if="consumoPeriodo.carregando"
              class="text-sm text-n-slate-11"
            >
              {{ $t('CAPITAO_SETTINGS.CONSUMO_IA.CARREGANDO') }}
            </div>

            <template v-else>
              <div class="flex flex-wrap items-baseline gap-x-5 gap-y-1">
                <span class="text-xl font-medium tabular-nums text-n-slate-12">
                  US$ {{ consumoPeriodo.total_usd.toFixed(2) }}
                </span>
                <span class="text-sm tabular-nums text-n-slate-11">
                  {{
                    $t('CAPITAO_SETTINGS.CONSUMO_IA.PERIODO_CHAMADAS', {
                      n: consumoPeriodo.total_chamadas,
                    })
                  }}
                </span>
              </div>

              <div
                v-if="!diasComGasto.length"
                class="text-sm text-n-slate-11"
              >
                {{ $t('CAPITAO_SETTINGS.CONSUMO_IA.PERIODO_VAZIO') }}
              </div>
              <!-- Do maior para o menor: é assim que se acha o dia fora da curva. -->
              <div v-else class="flex flex-col gap-1">
                <div
                  v-for="d in diasComGasto"
                  :key="d.dia"
                  class="flex items-baseline justify-between gap-3 text-sm"
                >
                  <span class="tabular-nums text-n-slate-11">
                    {{ dataCurta(d.dia) }}
                  </span>
                  <span class="flex-1 h-px bg-n-weak" />
                  <span class="tabular-nums text-n-slate-11">
                    {{
                      $t('CAPITAO_SETTINGS.CONSUMO_IA.PERIODO_CHAMADAS', {
                        n: d.chamadas,
                      })
                    }}
                  </span>
                  <span class="font-medium tabular-nums text-n-slate-12">
                    US$ {{ d.gasto_usd.toFixed(2) }}
                  </span>
                </div>
              </div>

              <p
                v-if="consumoIa && consumoIa.dias_guardados"
                class="text-xs text-n-slate-10"
              >
                {{
                  $t('CAPITAO_SETTINGS.CONSUMO_IA.JANELA', {
                    dias: consumoIa.dias_guardados,
                  })
                }}
              </p>
            </template>
          </div>
        </section>

        <!-- T-211 — a pergunta do Bira sobre a própria precisão -->
        <section
          class="flex flex-col gap-3 p-5 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex flex-col gap-1">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ $t('CAPITAO_SETTINGS.AVALIACAO_BIRA.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('CAPITAO_SETTINGS.AVALIACAO_BIRA.SUBTITLE') }}
              </p>
            </div>
            <label class="flex items-center flex-shrink-0 gap-2 mt-1">
              <input
                v-model="avaliacaoBira.ativa"
                type="checkbox"
                :disabled="avaliacaoBira.salvando"
                @change="salvarAvaliacaoBira"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('CAPITAO_SETTINGS.AVALIACAO_BIRA.ENABLED') }}
              </span>
            </label>
          </div>
          <p class="text-xs text-n-slate-11">
            {{ $t('CAPITAO_SETTINGS.AVALIACAO_BIRA.PERGUNTA') }}
          </p>
        </section>

        <!-- R8.4 — Pesos da criticidade -->
        <section
          class="flex flex-col gap-4 p-5 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-col gap-1">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('CAPITAO_SETTINGS.PESOS.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ $t('CAPITAO_SETTINGS.PESOS.SUBTITLE') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ $t('CAPITAO_SETTINGS.PESOS.HINT') }}
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="flex flex-col gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('CAPITAO_SETTINGS.PESOS.SENTIMENTO') }}
              </span>
              <label
                v-for="key in Object.keys(pesos.sentimento)"
                :key="key"
                class="flex items-center justify-between gap-2"
              >
                <span class="text-sm text-n-slate-11">{{
                  SENTIMENTO_LABELS[key] || key
                }}</span>
                <input
                  v-model.number="pesos.sentimento[key]"
                  type="number"
                  min="0"
                  max="100"
                  class="w-20 rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
                />
              </label>
            </div>
            <div class="flex flex-col gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('CAPITAO_SETTINGS.PESOS.ASSUNTO') }}
              </span>
              <label
                v-for="key in Object.keys(pesos.assunto)"
                :key="key"
                class="flex items-center justify-between gap-2"
              >
                <span class="text-sm text-n-slate-11 capitalize">{{
                  assuntoTitulo(key)
                }}</span>
                <input
                  v-model.number="pesos.assunto[key]"
                  type="number"
                  min="0"
                  max="100"
                  class="w-20 rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
                />
              </label>
            </div>
            <div class="flex flex-col gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('CAPITAO_SETTINGS.PESOS.PRIORIDADE') }}
              </span>
              <label
                v-for="key in Object.keys(pesos.prioridade)"
                :key="key"
                class="flex items-center justify-between gap-2"
              >
                <span class="text-sm text-n-slate-11">{{
                  PRIORIDADE_LABELS[key] || key
                }}</span>
                <input
                  v-model.number="pesos.prioridade[key]"
                  type="number"
                  min="0"
                  max="100"
                  class="w-20 rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
                />
              </label>
            </div>
          </div>

          <div class="flex justify-end gap-3">
            <Button
              :label="$t('CAPITAO_SETTINGS.PESOS.RESTORE')"
              variant="faded"
              color="slate"
              size="sm"
              :is-loading="savingPesos"
              @click="restaurarPesos"
            />
            <Button
              :label="$t('CAPITAO_SETTINGS.PESOS.SAVE')"
              size="sm"
              :is-loading="savingPesos"
              @click="savePesos"
            />
          </div>
        </section>

        <!-- Playbook -->
        <section class="flex flex-col gap-4">
          <div
            v-if="versaoPlaybook"
            class="flex items-center justify-between gap-4 px-4 py-3 rounded-lg bg-n-amber-3 text-n-amber-11"
          >
            <span class="text-sm">
              {{ $t('CAPITAO_SETTINGS.PLAYBOOK.RASCUNHO.BANNER') }}
            </span>
            <Button
              :label="$t('CAPITAO_SETTINGS.PLAYBOOK.RASCUNHO.VOLTAR')"
              variant="faded"
              color="slate"
              size="sm"
              @click="irParaPlaybook('no_ar')"
            />
          </div>
          <div
            v-else-if="temRascunho"
            class="flex items-center justify-between gap-4 px-4 py-3 rounded-lg bg-n-alpha-2"
          >
            <span class="text-sm text-n-slate-11">
              {{ $t('CAPITAO_SETTINGS.PLAYBOOK.RASCUNHO.EXISTE') }}
            </span>
            <Button
              :label="$t('CAPITAO_SETTINGS.PLAYBOOK.RASCUNHO.EDITAR')"
              variant="faded"
              color="slate"
              size="sm"
              @click="irParaPlaybook('rascunho')"
            />
          </div>

          <div class="flex items-start justify-between gap-4">
            <div class="flex flex-col gap-1">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ $t('CAPITAO_SETTINGS.PLAYBOOK.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('CAPITAO_SETTINGS.PLAYBOOK.SUBTITLE') }}
              </p>
            </div>
            <Button
              :label="$t('CAPITAO_SETTINGS.PLAYBOOK.ADD')"
              icon="i-lucide-plus"
              size="sm"
              @click="openNew"
            />
          </div>

          <BaseTable
            :headers="tableHeaders"
            :items="entries"
            :no-data-message="$t('CAPITAO_SETTINGS.PLAYBOOK.EMPTY')"
          >
            <template #row="{ items }">
              <BaseTableRow
                v-for="entry in items"
                :key="entry.id"
                :item="entry"
              >
                <template #default>
                  <BaseTableCell>
                    <span class="text-body-main text-n-slate-12">
                      {{ entry.title }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell>
                    <span class="text-body-main text-n-slate-11">
                      {{ entry.category || '—' }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell>
                    <span class="text-body-main text-n-slate-11">
                      {{ audienceLabel(entry.audience) }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell>
                    <span
                      class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                      :class="
                        entry.enabled
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-alpha-2 text-n-slate-11'
                      "
                    >
                      {{
                        entry.enabled
                          ? $t('CAPITAO_SETTINGS.PLAYBOOK.ENABLED')
                          : $t('CAPITAO_SETTINGS.PLAYBOOK.DISABLED')
                      }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell align="end">
                    <div class="flex gap-3 justify-end flex-shrink-0">
                      <Button
                        icon="i-woot-edit-pen"
                        slate
                        sm
                        @click="openEdit(entry)"
                      />
                      <Button
                        icon="i-woot-bin"
                        slate
                        sm
                        class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                        @click="openDelete(entry)"
                      />
                    </div>
                  </BaseTableCell>
                </template>
              </BaseTableRow>
            </template>
          </BaseTable>
        </section>
      </div>
    </template>

    <!-- Form de criar/editar -->
    <woot-modal v-model:show="showForm" :on-close="closeForm">
      <div class="flex flex-col gap-4 p-8">
        <h2 class="text-lg font-medium text-n-slate-12">{{ formTitle }}</h2>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CAPITAO_SETTINGS.PLAYBOOK.FORM.TITLE_LABEL') }}
          </span>
          <input
            v-model="form.title"
            type="text"
            :placeholder="
              $t('CAPITAO_SETTINGS.PLAYBOOK.FORM.TITLE_PLACEHOLDER')
            "
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CAPITAO_SETTINGS.PLAYBOOK.FORM.CATEGORY_LABEL') }}
          </span>
          <input
            v-model="form.category"
            type="text"
            :placeholder="
              $t('CAPITAO_SETTINGS.PLAYBOOK.FORM.CATEGORY_PLACEHOLDER')
            "
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CAPITAO_SETTINGS.PLAYBOOK.FORM.CONTENT_LABEL') }}
          </span>
          <textarea
            v-model="form.content"
            rows="5"
            :placeholder="
              $t('CAPITAO_SETTINGS.PLAYBOOK.FORM.CONTENT_PLACEHOLDER')
            "
            class="w-full resize-y rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
        </label>
        <div class="flex items-center gap-4">
          <label class="flex flex-col gap-1 flex-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('CAPITAO_SETTINGS.PLAYBOOK.FORM.AUDIENCE_LABEL') }}
            </span>
            <select
              v-model="form.audience"
              class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
            >
              <option v-for="a in AUDIENCES" :key="a" :value="a">
                {{ audienceLabel(a) }}
              </option>
            </select>
          </label>
          <label class="flex items-center gap-2 mt-6">
            <input v-model="form.enabled" type="checkbox" />
            <span class="text-sm text-n-slate-12">
              {{ $t('CAPITAO_SETTINGS.PLAYBOOK.FORM.ENABLED_LABEL') }}
            </span>
          </label>
        </div>
        <div class="flex justify-end gap-3 mt-2">
          <Button
            :label="$t('CAPITAO_SETTINGS.PLAYBOOK.FORM.CANCEL')"
            variant="faded"
            color="slate"
            size="sm"
            @click="closeForm"
          />
          <Button
            :label="$t('CAPITAO_SETTINGS.PLAYBOOK.FORM.SAVE')"
            size="sm"
            :disabled="!isFormValid"
            :is-loading="savingForm"
            @click="saveEntry"
          />
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDelete"
      :on-close="closeDelete"
      :on-confirm="confirmDelete"
      :title="$t('CAPITAO_SETTINGS.PLAYBOOK.DELETE.CONFIRM')"
      :message="selected ? selected.title : ''"
      :confirm-text="$t('CAPITAO_SETTINGS.PLAYBOOK.DELETE.YES')"
      :reject-text="$t('CAPITAO_SETTINGS.PLAYBOOK.DELETE.NO')"
    />
  </SettingsLayout>
</template>
